import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {
  ClientQueryParams,
  createQueryParams,
  IClient,
  IInsurer,
  IPaginatedResponse,
  IParnerQueryParams,
  IPartner,
  IPartnerMotorPremiumLimit,
  IPartnerPremiumLimitRequest,
  IPartnerProducts,
  IPartnerRequest,
  IPartnersStats,
  IPartnerStats,
  IPartnerSettings,
  IPartnerStatsQueryParams,
  IPartnerSystemPermissions,
  IPartnerTransactionsParams,
  IProduct,
  IProductsQuery,
  IQueryParams,
  IRepresentative,
  ITransaction,
  IUpdatePartner,
  PaginatedResponse,
  PaymentMethod, IRepresentativeRequest,
} from '@inshuwa-client/models';
import { baseUrl } from 'libs/core/src/lib/configs/baseUrl';
import {NgHttpCachingHeaders} from 'ng-http-caching';
import { sessionStore } from '../utils/session-store';

@Injectable({
  providedIn: 'root',
})
export class PartnersService {
  constructor(private http: HttpClient) {
  }

  public getPartnerById(id: string): Observable<IPartner> {
    return this.http.get<IPartner>(`${baseUrl()}/partners/${id}`);
  }


  getPartners(
    queryParams?: Partial<IParnerQueryParams>,
  ): Observable<IPaginatedResponse<IPartner>> {
    return this.http.get<IPaginatedResponse<IPartner>>(
      `${baseUrl()}/partners`,
      {params: createQueryParams(queryParams)},
    );
  }

  getPartnerStat(
    queryParams?: Partial<IParnerQueryParams>,
  ): Observable<IPartnerStats> {
    return this.http.get<IPartnerStats>(`${baseUrl()}/partners/statistics`, {
      params: createQueryParams(queryParams),
    });
  }

  addPartner(request: Partial<IPartnerRequest>): Observable<IPartner> {
    return this.http.post<IPartner>(`${baseUrl()}/partners`, request);
  }

  allocatePartner(
    request: Partial<IPartnerProducts>,
  ): Observable<IPartnerProducts> {
    return this.http.post<IPartnerProducts>(
      `${baseUrl()}/partners/products`,
      request,
    );
  }

  getById(id: any): Observable<IPartner> {
    return this.http.get<IPartner>(`${baseUrl()}/partners/${id}`);
  }

  getStats(
    id: number,
    params?: IPartnerStatsQueryParams,
  ): Observable<IPartnersStats> {
    return this.http.get<IPartnersStats>(
      `${baseUrl()}/partners/${id}/statistics`,
      {params: createQueryParams(params)},
    );
  }


  getPartnerClientById(
    id: number,
    params: ClientQueryParams,
  ): Observable<PaginatedResponse<IClient>> {
    return this.http.get<PaginatedResponse<IClient>>(
      `${baseUrl()}/partners/${id}/clients`,
      {
        params: createQueryParams(params),
      },
    );
  }

  brokerPartnerExists(params: {
    partnerId: number;
    brokerId: number;
  }): Observable<{ message: string; status: boolean }> {
    return this.http.get<{ message: string; status: boolean }>(
      `${baseUrl()}/partners/${params.partnerId}/brokers/${params.brokerId}/exists`,
      {
        params: createQueryParams(params),
      },
    );
  }

  addUser(
    partnerId: number, data?: Partial<IRepresentativeRequest> | undefined,
  ): Observable<IRepresentative> {
    return this.http.post<IRepresentative>(
      `${baseUrl()}/partners/${partnerId}/representatives`,
      {
        partnerId: partnerId,
        ...data,
      },
    );
  }

  updatePartner(
    id: number,
    payload: Partial<IUpdatePartner>,
  ): Observable<IPartner> {
    return this.http.put<IPartner>(`${baseUrl()}/partners/${id}`, payload);
  }

  updateSettings(
    id: number,
    payload: Partial<IPartnerSettings>,
  ): Observable<IPartnerSettings> {
    return this.http.put<IPartnerSettings>(
      `${baseUrl()}/partners/${id}/settings`,
      payload,
    );
  }

  /**
   * System permissions granted to a partner, one entry per insurer. The backend scopes the result to
   * what the caller may see, so an insurer never reads another insurer's arrangements.
   */
  getSystemPermissions(
    id: number,
    insurerId?: number | null,
  ): Observable<IPartnerSystemPermissions[]> {
    return this.http.get<IPartnerSystemPermissions[]>(
      `${baseUrl()}/partners/${id}/system-permissions`,
      {params: insurerId ? {insurerId} : {}},
    );
  }

  /**
   * States the whole grant for one insurer: permissions left false are written as false, so revoking
   * is as explicit as granting.
   */
  updateSystemPermissions(
    id: number,
    payload: IPartnerSystemPermissions,
  ): Observable<IPartnerSystemPermissions> {
    return this.http.put<IPartnerSystemPermissions>(
      `${baseUrl()}/partners/${id}/system-permissions`,
      payload,
    );
  }


  getPartnerRepresentatives(
    Id: number,
    queryParams?: Partial<IQueryParams>,
    noCache: boolean = false,
  ): Observable<IPaginatedResponse<IRepresentative>> {
    const HttpCatchingHeaders = noCache
      ? NgHttpCachingHeaders.DISALLOW_CACHE
      : NgHttpCachingHeaders.ALLOW_CACHE;
    return this.http.get<IPaginatedResponse<IRepresentative>>(
      `${baseUrl()}/partners/${Id}/representatives`,
      {
        params: createQueryParams(queryParams),
        headers: {
          [HttpCatchingHeaders]: '1',
        },
      },
    );
  }

  assignPartnerToBroker(
    brokerId: number,
    partnerId: number,
    paymentMethods?: PaymentMethod[],
  ): Observable<IPartner> {
    return this.http.post<IPartner>(
      `${baseUrl()}/brokers/${brokerId}/partners/${partnerId}`,
      {
        paymentMethods: paymentMethods,
        brokerId: brokerId,
        partnerId: partnerId,
      },
    );
  }

  assignPartnerToInsurer(
    insurerId: number,
    partnerId: number,
    paymentMethods?: PaymentMethod[],
  ): Observable<IPartner> {
    return this.http.post<IPartner>(
      `${baseUrl()}/insurers/${insurerId}/partners/${partnerId}`,
      {
        params: createQueryParams({
          paymentMethods: paymentMethods,
        }),
      },
    );
  }

  unassignPartnerFromInsurer(
    insurerId: number,
    partnerId: number,
  ): Observable<void> {
    return this.http.delete<void>(
      `${baseUrl()}/insurers/${insurerId}/partners/${partnerId}`,
    );
  }

  getMotorPremiumLimits(
    request: IPartnerPremiumLimitRequest,
  ): Observable<IPartnerMotorPremiumLimit[]> {
    const {partnerId} = request;
    return this.http.get<IPartnerMotorPremiumLimit[]>(
      `${baseUrl()}/intermediaries/premium-limits/motor`,
      {
        params: createQueryParams({
          insurerId: request.insurerId,
          brokerId: request.brokerId,
          partnerId: request.partnerId,
        }),
        headers: {
          [NgHttpCachingHeaders.DISALLOW_CACHE]: '1',
        },
      },
    );
  }

  addMotorPremiumLimit(
    limit: Partial<IPartnerMotorPremiumLimit>,
  ): Observable<IPartnerMotorPremiumLimit> {
    return this.http.post<IPartnerMotorPremiumLimit>(
      `${baseUrl()}/intermediaries/premium-limits/motor`,
      limit,
    );
  }

  saveToLocalStorage(partner: IPartner) {
    sessionStore.setItem('partner', JSON.stringify(partner));
  }

  getFromLocalStorage() {
    const partner = sessionStore.getItem('partner');
    return partner ? JSON.parse(partner) : null;
  }
}
